/**
 * Date + time-slot picker when adding a Viator tour from Day View — mirrors iOS TourDatePickerSheet.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Segmented, Button } from 'antd';
import { tourSearchEn } from '../../content/trips.en';
import { formatDayChip } from '../../content/cityPlanning.en';
import type { ViatorProduct } from '../../services/viatorProductsService';
import {
  enumerateVisitDates,
  VIATOR_TOUR_DURATION_HOURS,
  type TourTimeSlot,
} from '../../utils/tourItemSave';
import { ymdFromUnknown } from '../../utils/wallClock';
import styles from './TourDateSlotModal.module.css';

export interface TourDateSlotSelection {
  dateYmd: string;
  slot: TourTimeSlot;
}

interface TourDateSlotModalProps {
  open: boolean;
  tour: ViatorProduct | null;
  arrivalDate?: string | null;
  departureDate?: string | null;
  initialDate: string;
  adding?: boolean;
  onCancel: () => void;
  onConfirm: (selection: TourDateSlotSelection) => void;
}

const SLOT_OPTIONS = [
  { label: tourSearchEn.slotMorning, value: 'morning' as const },
  { label: tourSearchEn.slotAfternoon, value: 'afternoon' as const },
  { label: tourSearchEn.slotEvening, value: 'evening' as const },
  { label: tourSearchEn.slotNight, value: 'night' as const },
];

const TourDateSlotModal: React.FC<TourDateSlotModalProps> = ({
  open,
  tour,
  arrivalDate,
  departureDate,
  initialDate,
  adding = false,
  onCancel,
  onConfirm,
}) => {
  const dates = useMemo(
    () => enumerateVisitDates(arrivalDate, departureDate, initialDate),
    [arrivalDate, departureDate, initialDate]
  );

  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [slot, setSlot] = useState<TourTimeSlot>('morning');

  useEffect(() => {
    if (!open) return;
    const initial = ymdFromUnknown(initialDate) ?? dates[0];
    const preferred = dates.includes(initial) ? initial : dates[0];
    setSelectedDate(preferred);
    setSlot('morning');
  }, [open, initialDate, dates]);

  if (!tour) return null;

  const priceLabel =
    tour.price?.startingPrice != null
      ? `${tour.price.currency} ${tour.price.startingPrice.toFixed(0)}`
      : null;

  const handleConfirm = (): void => {
    const dateYmd = ymdFromUnknown(selectedDate) ?? selectedDate;
    onConfirm({ dateYmd, slot });
  };

  return (
    <Modal
      open={open}
      title={tourSearchEn.pickerTitle}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      width={440}
      className={styles.modal}
    >
      <div className={styles.body}>
        <div className={styles.header}>
          <div className={styles.title}>{tour.title}</div>
          <div className={styles.meta}>
            <span>{tourSearchEn.durationHours.replace('{hours}', String(VIATOR_TOUR_DURATION_HOURS))}</span>
            {priceLabel ? <span>{priceLabel}</span> : null}
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionLabel}>{tourSearchEn.pickerDay}</div>
          <div className={styles.dayChips} role="listbox" aria-label={tourSearchEn.pickerDay}>
            {dates.map((ymd) => (
              <button
                key={ymd}
                type="button"
                role="option"
                aria-selected={ymd === selectedDate}
                className={ymd === selectedDate ? styles.dayChipActive : styles.dayChip}
                onClick={() => setSelectedDate(ymd)}
              >
                {formatDayChip(ymd)}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionLabel}>{tourSearchEn.pickerTime}</div>
          <Segmented
            block
            options={SLOT_OPTIONS}
            value={slot}
            onChange={(value) => setSlot(value as TourTimeSlot)}
          />
        </div>

        <div className={styles.actions}>
          <Button onClick={onCancel} disabled={adding}>
            {tourSearchEn.cancel}
          </Button>
          <Button
            type="primary"
            loading={adding}
            onClick={handleConfirm}
          >
            {tourSearchEn.addTour}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default TourDateSlotModal;
